import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Copy, Check, ExternalLink, Leaf, Trophy, TreePalm, Share2, Code, QrCode, Camera, Search, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SellerProfilePage = () => {
  const { sellerId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedEmbed, setCopiedEmbed] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  
  // QR Scanner state
  const videoRef = useRef(null);
  const [scanMode, setScanMode] = useState('manual'); // manual, camera
  const [stream, setStream] = useState(null);
  const [certificateId, setCertificateId] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, [sellerId]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/seller/profile/${sellerId}`);
      setProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Seller profile not found');
    } finally {
      setLoading(false);
    }
  };

  // QR Scanner Functions
  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment', width: 640, height: 480 } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setStream(mediaStream);
      setScanMode('camera');
    } catch (error) {
      toast.error('Camera access denied. Please use manual entry.');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setScanMode('manual');
  };

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  const verifyProduct = async (certId) => {
    if (!certId.trim()) {
      toast.error('Please enter a certificate ID');
      return;
    }
    
    setVerifying(true);
    setVerifyResult(null);
    try {
      // Send as JSON, not FormData
      const response = await axios.post(`${API}/scan/verify`, {
        certificate_id: certId.trim()
      });
      setVerifyResult(response.data);
      
      if (response.data.verified) {
        toast.success('Product verified!');
      } else {
        toast.error('Product not found');
      }
    } catch (error) {
      setVerifyResult({ verified: false, message: 'Verification failed. Please check the certificate ID.' });
      toast.error('Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const handleManualVerify = (e) => {
    e.preventDefault();
    verifyProduct(certificateId);
  };

  const handleCameraScan = () => {
    toast.info('Scanning... (Position QR code in frame)');
    // In production, use a QR library like @zxing/browser
    // For now, simulate scan
    setTimeout(() => {
      stopCamera();
      toast.info('Demo: Enter certificate ID manually');
    }, 2000);
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'embed') {
        setCopiedEmbed(true);
        setTimeout(() => setCopiedEmbed(false), 2000);
      } else {
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
      }
      toast.success('Copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const getTierColor = (tier) => {
    const colors = {
      platinum: 'bg-slate-100 text-slate-800 border-slate-300',
      gold: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      silver: 'bg-gray-100 text-gray-800 border-gray-300',
      bronze: 'bg-orange-100 text-orange-800 border-orange-300',
      uncertified: 'bg-gray-100 text-gray-500 border-gray-200'
    };
    return colors[tier] || colors.uncertified;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A6C4C] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading seller profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-gray-600 mb-4">Seller profile not found</p>
            <Button onClick={() => navigate('/')}>Go Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <Link to="/" className="flex items-center gap-2">
            <Leaf className="w-6 h-6 text-[#4A6C4C]" />
            <span className="font-bold text-[#4A6C4C]">GinsGood</span>
          </Link>
          <div className="w-20"></div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Profile Header */}
        <Card className="mb-8 overflow-hidden">
          <div className="bg-gradient-to-r from-[#4A6C4C] to-[#6B8E6B] p-8 text-white">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-2xl">
                    {profile.name?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold">{profile.name}</h1>
                    <p className="text-green-100 capitalize">{profile.user_type}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-4">
                  <Badge className="bg-white/20 text-white border-white/30">
                    {profile.stats.tier}
                  </Badge>
                  <Badge className="bg-white/20 text-white border-white/30">
                    {profile.stats.gsg_balance.toLocaleString()} GSG
                  </Badge>
                </div>
              </div>
              <div className="text-right">
                <div className="text-4xl font-bold">{profile.stats.total_certifications}</div>
                <div className="text-green-100">Certified Products</div>
              </div>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 bg-gray-50">
            <div className="text-center">
              <div className="text-2xl font-bold text-[#4A6C4C]">{profile.stats.average_score.toFixed(1)}%</div>
              <div className="text-sm text-gray-600">Avg Score</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-[#4A6C4C]">{profile.stats.total_carbon_offset_kg}</div>
              <div className="text-sm text-gray-600">kg CO2 Offset</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {profile.stats.tier_distribution.gold + profile.stats.tier_distribution.platinum}
              </div>
              <div className="text-sm text-gray-600">Gold+ Products</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{profile.stats.gsg_balance.toLocaleString()}</div>
              <div className="text-sm text-gray-600">GSG Earned</div>
            </div>
          </div>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="products" className="space-y-6">
          <TabsList className="grid w-full max-w-lg grid-cols-4">
            <TabsTrigger value="products" data-testid="products-tab">Products</TabsTrigger>
            <TabsTrigger value="verify" data-testid="verify-tab">Verify</TabsTrigger>
            <TabsTrigger value="share" data-testid="share-tab">Share</TabsTrigger>
            <TabsTrigger value="embed" data-testid="embed-tab">Embed</TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {profile.certificates.length === 0 ? (
                <Card className="col-span-full">
                  <CardContent className="pt-6 text-center text-gray-500">
                    No certified products yet
                  </CardContent>
                </Card>
              ) : (
                profile.certificates.map((cert) => (
                  <Card key={cert.id} className="hover:shadow-lg transition-shadow cursor-pointer" 
                        onClick={() => navigate(`/passport/${cert.id}`)}
                        data-testid={`product-card-${cert.id}`}>
                    <CardContent className="p-4">
                      {cert.product_image_url && (
                        <div className="aspect-square bg-gray-100 rounded-lg mb-3 overflow-hidden">
                          <img 
                            src={cert.product_image_url} 
                            alt={cert.product_name}
                            className="w-full h-full object-cover"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        </div>
                      )}
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">{cert.product_name}</h3>
                          <p className="text-sm text-gray-500 truncate">{cert.brand}</p>
                        </div>
                        <Badge className={`${getTierColor(cert.tier)} shrink-0`}>
                          {cert.tier_icon} {cert.tier_display}
                        </Badge>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-2xl font-bold text-[#4A6C4C]">{cert.score?.toFixed(1)}%</span>
                        <span className="text-xs text-gray-400">
                          {new Date(cert.certified_at).toLocaleDateString()}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Verify Tab - QR Scanner */}
          <TabsContent value="verify">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <QrCode className="w-5 h-5" />
                  Verify Product
                </CardTitle>
                <CardDescription>
                  Scan a QR code or enter a certificate ID to verify any product from this seller
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Scanner Mode Toggle */}
                <div className="flex gap-2">
                  <Button 
                    variant={scanMode === 'manual' ? 'default' : 'outline'}
                    className="flex-1 gap-2"
                    onClick={() => { stopCamera(); setScanMode('manual'); }}
                  >
                    <Search className="w-4 h-4" />
                    Enter ID
                  </Button>
                  <Button 
                    variant={scanMode === 'camera' ? 'default' : 'outline'}
                    className="flex-1 gap-2"
                    onClick={startCamera}
                  >
                    <Camera className="w-4 h-4" />
                    Scan QR
                  </Button>
                </div>

                {/* Manual Entry */}
                {scanMode === 'manual' && (
                  <form onSubmit={handleManualVerify} className="space-y-4">
                    <Input
                      value={certificateId}
                      onChange={(e) => setCertificateId(e.target.value)}
                      placeholder="Enter Certificate ID (e.g., ad5c06b0-5a1d-4c04-9945-eac5b7b81a2e)"
                      className="h-12"
                      data-testid="verify-cert-input"
                    />
                    <Button 
                      type="submit" 
                      className="w-full h-12 gap-2 bg-[#4A6C4C] hover:bg-[#3d5940]"
                      disabled={verifying}
                    >
                      {verifying ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      ) : (
                        <Search className="w-5 h-5" />
                      )}
                      Verify Product
                    </Button>
                  </form>
                )}

                {/* Camera Scanner */}
                {scanMode === 'camera' && (
                  <div className="space-y-4">
                    <div className="relative aspect-video bg-black rounded-xl overflow-hidden">
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-full object-cover"
                      />
                      {/* Scan overlay */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-48 h-48 border-2 border-white/50 rounded-xl relative">
                          <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-[#4A6C4C] rounded-tl-lg" />
                          <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-[#4A6C4C] rounded-tr-lg" />
                          <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-[#4A6C4C] rounded-bl-lg" />
                          <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-[#4A6C4C] rounded-br-lg" />
                        </div>
                      </div>
                    </div>
                    <p className="text-center text-sm text-gray-500">
                      Point camera at product QR code
                    </p>
                    <div className="flex gap-4">
                      <Button variant="outline" onClick={stopCamera} className="flex-1">
                        Cancel
                      </Button>
                      <Button onClick={handleCameraScan} className="flex-1 gap-2 bg-[#4A6C4C] hover:bg-[#3d5940]">
                        <QrCode className="w-5 h-5" />
                        Capture
                      </Button>
                    </div>
                  </div>
                )}

                {/* Verification Result */}
                {verifyResult && (
                  <div className="pt-6 border-t">
                    {verifyResult.verified ? (
                      <div className="text-center space-y-4">
                        <div className="w-16 h-16 mx-auto bg-[#4A6C4C]/10 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-8 h-8 text-[#4A6C4C]" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-[#4A6C4C]">Verified Product</h3>
                          <p className="text-sm text-gray-500">{verifyResult.message}</p>
                        </div>
                        
                        <Card className="p-4 text-left bg-gray-50">
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-500">Product</span>
                              <span className="font-medium">{verifyResult.product_name}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Certificate</span>
                              <span className="font-mono text-xs">{verifyResult.certificate_number}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Score</span>
                              <span className="font-bold text-[#4A6C4C]">{verifyResult.ginsgood_score}%</span>
                            </div>
                            {verifyResult.artisan && (
                              <div className="flex justify-between">
                                <span className="text-gray-500">Artisan</span>
                                <span>{verifyResult.artisan}</span>
                              </div>
                            )}
                          </div>
                        </Card>

                        <Button 
                          onClick={() => navigate(verifyResult.passport_url)} 
                          className="w-full gap-2 bg-[#4A6C4C] hover:bg-[#3d5940]"
                        >
                          View Full Passport
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center space-y-4">
                        <div className="w-16 h-16 mx-auto bg-red-50 rounded-full flex items-center justify-center">
                          <XCircle className="w-8 h-8 text-red-500" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-red-600">Not Verified</h3>
                          <p className="text-sm text-gray-500">{verifyResult.message}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Quick Verify Links */}
                {profile.certificates.length > 0 && !verifyResult && (
                  <div className="pt-4 border-t">
                    <p className="text-sm text-gray-500 mb-3">Quick verify this seller's products:</p>
                    <div className="flex flex-wrap gap-2">
                      {profile.certificates.slice(0, 3).map((cert) => (
                        <Button
                          key={cert.id}
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setCertificateId(cert.id);
                            verifyProduct(cert.id);
                          }}
                          className="text-xs"
                        >
                          {cert.product_name.substring(0, 20)}...
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Share Tab */}
          <TabsContent value="share">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Share2 className="w-5 h-5" />
                  Share Your Profile
                </CardTitle>
                <CardDescription>
                  Share your GinsGood seller profile on social media or add to your Tudugu store
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Profile Link */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Profile Link</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={`${window.location.origin}/seller/${profile.iris_id}`}
                      className="flex-1 p-2 border rounded-lg bg-gray-50 text-sm"
                    />
                    <Button 
                      variant="outline" 
                      onClick={() => copyToClipboard(`${window.location.origin}/seller/${profile.iris_id}`, 'link')}
                      data-testid="copy-link-btn"
                    >
                      {copiedLink ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                {/* Share Text */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Share Text</label>
                  <textarea
                    readOnly
                    value={profile.share_text}
                    className="w-full p-3 border rounded-lg bg-gray-50 text-sm h-24 resize-none"
                  />
                  <Button 
                    variant="outline" 
                    className="mt-2"
                    onClick={() => copyToClipboard(profile.share_text, 'text')}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Text
                  </Button>
                </div>

                {/* Social Buttons */}
                <div className="flex gap-3">
                  <Button 
                    className="flex-1 bg-[#1DA1F2] hover:bg-[#1a8cd8]"
                    onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(profile.share_text)}`, '_blank')}
                  >
                    Share on Twitter
                  </Button>
                  <Button 
                    className="flex-1 bg-[#0A66C2] hover:bg-[#084d92]"
                    onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`${window.location.origin}/seller/${profile.iris_id}`)}`, '_blank')}
                  >
                    Share on LinkedIn
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Embed Tab */}
          <TabsContent value="embed">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Code className="w-5 h-5" />
                  Embed on Tudugu
                </CardTitle>
                <CardDescription>
                  Add this code to your Tudugu store description or product pages to show your GinsGood certification
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Profile Badge Embed */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Seller Profile Badge</label>
                  <p className="text-sm text-gray-500 mb-3">
                    Add this to your Tudugu store bio or about section
                  </p>
                  <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                    <pre className="text-green-400 text-sm whitespace-pre-wrap break-all">
                      {profile.embed_code}
                    </pre>
                  </div>
                  <Button 
                    variant="outline" 
                    className="mt-3"
                    onClick={() => copyToClipboard(profile.embed_code, 'embed')}
                    data-testid="copy-embed-btn"
                  >
                    {copiedEmbed ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                    Copy Embed Code
                  </Button>
                </div>

                {/* Preview */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Preview</label>
                  <div className="border rounded-lg p-4 bg-white inline-block">
                    {/* Safe preview without dangerouslySetInnerHTML */}
                    <a 
                      href={`${window.location.origin}/seller/${profile.iris_id}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block font-sans p-3 border border-gray-200 rounded-lg bg-white no-underline text-inherit"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xl">🌿</span>
                        <span className="font-semibold text-[#4A6C4C]">GINSGOOD SELLER</span>
                      </div>
                      <div className="font-medium text-gray-700">{profile.name || 'Seller'}</div>
                      <div className="text-sm text-gray-500">{profile.stats?.total_certifications || 0} certified products</div>
                      <div className="text-sm text-gray-500">Avg Score: {(profile.stats?.average_score || 0).toFixed(1)}%</div>
                    </a>
                  </div>
                </div>

                {/* Individual Product Badges */}
                <div className="border-t pt-6">
                  <h3 className="font-medium text-gray-900 mb-2">Product Certification Badges</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    For individual product pages, use this script and add the data attribute to any element:
                  </p>
                  <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto mb-4">
                    <pre className="text-green-400 text-sm">
{`<!-- Add once in your page -->
<script src="${window.location.origin}/api/embed/script.js"></script>

<!-- Add to each product -->
<div data-ginsgood-cert="YOUR_CERTIFICATE_ID"></div>`}
                    </pre>
                  </div>
                  <Button 
                    variant="outline"
                    onClick={() => copyToClipboard(`<script src="${window.location.origin}/api/embed/script.js"></script>\n\n<div data-ginsgood-cert="YOUR_CERTIFICATE_ID"></div>`, 'script')}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Script Code
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Tier Distribution */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              Certification Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-5 gap-2 text-center">
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="text-xl font-bold text-slate-600">{profile.stats.tier_distribution.platinum}</div>
                <div className="text-xs text-gray-500">Platinum</div>
              </div>
              <div className="p-3 bg-yellow-50 rounded-lg">
                <div className="text-xl font-bold text-yellow-600">{profile.stats.tier_distribution.gold}</div>
                <div className="text-xs text-gray-500">Gold</div>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="text-xl font-bold text-gray-600">{profile.stats.tier_distribution.silver}</div>
                <div className="text-xs text-gray-500">Silver</div>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg">
                <div className="text-xl font-bold text-orange-600">{profile.stats.tier_distribution.bronze}</div>
                <div className="text-xs text-gray-500">Bronze</div>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="text-xl font-bold text-gray-400">{profile.stats.tier_distribution.uncertified}</div>
                <div className="text-xs text-gray-500">Pending</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer CTA */}
        <Card className="mt-8 bg-gradient-to-r from-[#4A6C4C] to-[#6B8E6B] text-white">
          <CardContent className="py-8 text-center">
            <TreePalm className="w-12 h-12 mx-auto mb-4 opacity-80" />
            <h3 className="text-xl font-bold mb-2">Want to certify your products?</h3>
            <p className="text-green-100 mb-4">Join GinsGood and start building trust with your customers</p>
            <Button 
              variant="secondary" 
              className="bg-white text-[#4A6C4C] hover:bg-gray-100"
              onClick={() => navigate('/iris-auth')}
            >
              Get Started Free
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default SellerProfilePage;
